/**
 * ArrangementPhotoCard — photo viewer for the mobile Design brief.
 *
 * Shows a dashed "waiting for phone" placeholder when no photo exists.
 * Once a photo is uploaded from mobile, shows the thumbnail with a timestamp,
 * an "open full" lightbox button, and a delete button (permission-gated).
 *
 * Photo discovery and writes belong to the shared per-order workflow.
 */
import React, { useState } from 'react';
import { Camera, Trash2, Maximize2, X } from 'lucide-react';
import { useArrangementPhoto } from '@/hooks/useArrangementPhoto';
import ArrangementPhotoStatus from '@/components/ArrangementPhotoStatus';
import { offlineMediaLabel } from '@/utils/mediaPresentation';

export function ArrangementPhotoCard({ orderId, initialPhoto, assignedDesignerId }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const workflow = useArrangementPhoto(orderId, initialPhoto, { assignedDesignerId });
  const { photo, imageResult, imageUrl, busy, deleting, canDelete, remove } = workflow;

  const handleDelete = () => {
    if (!canDelete || busy) return;
    const displayedPhoto = photo;
    if (!window.confirm('Delete this arrangement photo?')) return;
    void remove(displayedPhoto);
  };

  const formattedTime = photo?.uploaded_at
    ? new Date(photo.uploaded_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <>
      <div className="border border-stone-200 rounded-lg bg-white overflow-hidden" data-testid="arrangement-photo-card">
        <div className="flex items-center justify-between px-5 py-2.5 bg-sky-100/60">
          <span className="text-xs font-bold text-stone-600 uppercase tracking-wider">
            Arrangement Photo
          </span>
          {formattedTime && (
            <span className="text-xs text-stone-400">{formattedTime} · ✓</span>
          )}
        </div>

        {!photo ? (
          <div
            className="mx-4 my-4 flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed border-stone-200 rounded-lg bg-stone-50/50"
            data-testid="arrangement-photo-waiting"
          >
            <Camera className="h-7 w-7 text-stone-300" />
            <span className="text-sm font-medium text-stone-400">waiting for phone</span>
            <span className="text-xs text-stone-300">captured in LVB Designer (mobile)</span>
          </div>
        ) : (
          <div className="px-5 py-4" data-testid="arrangement-photo-captured">
            <div className="relative rounded-lg overflow-hidden bg-stone-100 aspect-[4/3]">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Arrangement"
                  className="w-full h-full object-cover"
                />
              ) : imageResult?.state !== 'loading' ? (
                <div className="w-full h-full flex items-center justify-center text-xs text-stone-400 bg-stone-100">
                  {offlineMediaLabel(imageResult)}
                </div>
              ) : (
                <div className="w-full h-full animate-pulse bg-stone-200" />
              )}
            </div>
            <div className="flex items-center justify-end gap-3 mt-2">
              {imageUrl && (
                <button
                  onClick={() => setLightboxOpen(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-stone-800 transition-colors"
                  data-testid="arrangement-photo-open-full"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                  open full
                </button>
              )}
              {canDelete && (
                <button
                  onClick={handleDelete}
                  disabled={busy}
                  className="flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-600 transition-colors disabled:opacity-40"
                  data-testid="arrangement-photo-delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {deleting ? 'deleting…' : 'delete'}
                </button>
              )}
            </div>
          </div>
        )}
        <div className="px-4"><ArrangementPhotoStatus workflow={workflow} /></div>
      </div>

      {lightboxOpen && imageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          data-testid="arrangement-photo-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={imageUrl}
            alt="Arrangement full size"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

export default ArrangementPhotoCard;
